import { SessionCart, useCart } from 'lib/cart';
import { useState } from 'react';
import { getOrderById } from './api';
import { OrderAPIResponse, OrderLineAPI } from './interface';

const useReorder = () => {
  const { refreshCart, popCartInUse, addToCart } = useCart();
  const [isReplicating, setIsReplicating] = useState(false);

  const _addToCart = async (line: OrderLineAPI) => {
    try {
      await addToCart({
        product: line.product,
        productVariantId: line.product_variant_id,
        quantity: line.quantity,
        addonOptionValueIds:
          line?.components?.addon_option_values.map(
            (addonOptionValue) => addonOptionValue.id,
          ) ?? [],
      });
    } catch (err) {
      throw err;
    }
  };

  const onReorder = async ({
    identifier,
    onError,
    onWarning,
    onSuccess,
  }: {
    identifier: string | OrderAPIResponse;
    onError?: (message: string) => void;
    onWarning?: (
      productNames: string[],
      order: OrderAPIResponse,
      isSomeProductUnPurchasable?: boolean,
    ) => void;
    onSuccess?: (order: OrderAPIResponse, message: string) => void;
  }) => {
    try {
      setIsReplicating(true);
      let order: OrderAPIResponse | null = null;
      if (typeof identifier === 'string') {
        const { data } = await getOrderById(identifier);
        order = data;
      } else {
        order = identifier;
      }

      if (!order) return;

      await SessionCart.createNewCart({ isGuest: false });
      await refreshCart();

      const addToCartResults = await Promise.allSettled(
        order.lines.map(async (line) => {
          return await _addToCart(line);
        }),
      );

      const isAddToCartFailed = (
        result: PromiseSettledResult<void>,
      ) => result.status === 'rejected';

      const failedAddToCartResults = addToCartResults.filter(
        isAddToCartFailed,
      );

      const isEveryOrderLineReorderFailed = addToCartResults.every(
        isAddToCartFailed,
      );

      const failedAddToCartProductNames = failedAddToCartResults.map(
        (failed) => {
          // @ts-ignore
          const error = failed.reason;
          const { product } = error;

          return product.name;
        },
      );

      const isUnPurchasableError = (failed: PromiseRejectedResult) =>
        failed.reason?.message === 'Product is not purchasable.';

      const unPurchasableResults = failedAddToCartResults.filter(
        isUnPurchasableError,
      );

      const isEveryProductUnPurchasable =
        failedAddToCartResults.length > 0 &&
        failedAddToCartResults.every(isUnPurchasableError);

      const isSomeProductUnPurchasable =
        unPurchasableResults.length > 0;

      if (isEveryOrderLineReorderFailed) {
        await popCartInUse();

        if (isEveryProductUnPurchasable) {
          onError?.(
            'These items were available in your previous order, but isn’t eligible for purchase under your account now. 😓',
          );

          return;
        }

        onError?.(
          'Aiyo! Everything you want to reorder is out of stock 😢 Please add other stuff from our menu!',
        );

        return;
      }

      const isSomeOrderLineReorderFailed = addToCartResults.some(
        isAddToCartFailed,
      );

      if (isSomeOrderLineReorderFailed) {
        onWarning?.(
          failedAddToCartProductNames,
          order,
          isSomeProductUnPurchasable,
        );

        return;
      }

      onSuccess?.(
        order,
        'Your reordered items have been added to cart! 🥳 You can now checkout!',
      );
      return;
    } catch (err) {
      await popCartInUse();
      onError?.(
        err.message ?? 'Something went wrong! Please try again.',
      );
    } finally {
      setIsReplicating(false);
    }
  };

  return {
    isReplicating,
    onReorder,
  };
};

export default useReorder;
